<template>
  <div class="flex gap-1 m-0 md:ml-2">
    <div v-if="showActionBadges" class="flex gap-1 items-start">
      <FavoritedBadge ref="favoritedBadge" v-if="isFavorited" />
      <SkippedBadge v-if="isSkipped" />
    </div>
    <div v-if="showActionButtons" class="flex gap-1 items-start">
      <FavoriteButton v-if="canFavorite" />
      <SkipButton v-if="canSkip" />
    </div>
    <Dropdown class="m-0">
      <div slot="toggle">
        <div
          class="text-gray-900 hover:bg-gray-100 px-7px md:px-2 py-1 rounded-md cursor-pointer ds-text-sm md:ds-text-base"
          data-test-id="toggle-cta"
        >
          <Icon name="ellipsis" />
        </div>
      </div>
      <div slot="content" class="ds-text-sm" data-test-id="content">
        <FavoriteActionItem v-if="showFavoriteActionItem" />
        <SkipActionItem v-if="showSkipActionItem" />
        <div v-if="showActionItemsSeparator" class="bg-gray-200 my-1" style="height:1px;" />
        <div @click="dropdownItemClick">
          <MessageActionItem />
        </div>
        <FeedbackRequestActionItem v-if="canSendFeedbackRequest" />
        <VideoRequestActionItem v-if="showVideoFeedbackActionItem" />
        <DropdownActionItem class="flex items-center" data-test-id="copy-link" @click="copyProfileLink">
          Copy profile link
        </DropdownActionItem>
      </div>
    </Dropdown>
    <Portal to="flash-alert">
      <div
        v-if="shouldShowAlert"
        class="z-1000 fixed bottom-3 right-3 left-3 xs:right-3 xs:left-auto"
      >
        <FlashAlert
          ref="flashAlert"
          type="success"
          :persistent="false"
          :message="alertMessage"
          @flash-alert-dismissed="resetAlert"
        />
      </div>
    </Portal>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Dropdown, DropdownActionItem, FlashAlert, Icon } from "@sportsrecruits/playbook"
import { mapGetters, mapState } from "vuex";
import FavoriteActionItem from "./CoachActions/FavoriteActionItem.vue";
import SkipActionItem from "./CoachActions/SkipActionItem.vue";
import MessageActionItem from "@/components/AthleteProfile/AthleteProfileV2/CoachActions/MessageActionItem.vue";
import FeedbackRequestActionItem
  from "@/components/AthleteProfile/AthleteProfileV2/CoachActions/FeedbackRequest/FeedbackRequestActionItem.vue";
import VideoRequestActionItem
  from "@/components/AthleteProfile/AthleteProfileV2/CoachActions/FeedbackRequest/VideoRequestActionItem.vue";
import FavoriteButton from "./CoachActions/FavoriteButton.vue";
import SkipButton from "./CoachActions/SkipButton.vue";
import FavoritedBadge from "./CoachActions/FavoritedBadge.vue";
import SkippedBadge from "./CoachActions/SkippedBadge.vue";

export default defineComponent({
  name: "CoachDropdown",
  components: {
    SkippedBadge,
    FavoritedBadge,
    SkipButton,
    FavoriteButton,
    FeedbackRequestActionItem,
    MessageActionItem,
    SkipActionItem,
    FavoriteActionItem,
    Dropdown,
    DropdownActionItem,
    Icon,
    FlashAlert,
    VideoRequestActionItem,
  },
  data() {
    return {
      shouldShowAlert: false,
      alertMessage: '',
    };
  },
  computed: {
    ...mapState({
      hasPrivateVideos: state => state.athleteProfile.user.hasPrivateVideos,
    }),
    ...mapGetters("athleteProfile", [
      "canFavorite",
      "canSkip",
      "canSendFeedbackRequest",
      "isSendingFeedbackRequest",
      "videos",
      'isFavorited',
      'isSkipped',
    ]),
    isMobile() {
      return ["xs", "sm", "mobile"].includes(this.$mq);
    },
    showVideoFeedbackActionItem() {
      return !this.videos?.length && !this.hasPrivateVideos && this.canSendFeedbackRequest;
    },
    showActionButtons() {
      return !this.isMobile && (this.canFavorite || this.canSkip) && !(this.isFavorited || this.isSkipped);
    },
    showActionBadges() {
      return this.isFavorited || this.isSkipped;
    },
    showFavoriteActionItem() {
      if (!this.isMobile) {
        return this.canFavorite && this.showActionBadges;
      }
      return this.canFavorite;
    },
    showSkipActionItem() {
      if (!this.isMobile) {
        return this.canSkip && this.showActionBadges;
      }
      return this.canSkip;
    },
    showActionItemsSeparator() {
      return this.showFavoriteActionItem || this.showSkipActionItem;
    },
  },
  watch: {
    isFavorited(is) {
      if (is) {
        this.showAlert('Favorited Athlete');
      }
    },
    isSkipped(is) {
      if (is) {
        this.showAlert('Skipped Athlete');
      }
    },
    isSendingFeedbackRequest(is) {
      if (is) {
        this.showAlert('Request has been sent');
      }
    },
  },
  methods: {
    copyProfileLink() {
      const url = window.location.href;
      navigator.clipboard.writeText(url);
      this.showAlert('Link Copied');
    },
    dropdownItemClick() {
      this.hideDropdown()
    },
    hideDropdown() {
      document.body.click();
    },
    showAlert(message) {
      this.resetAlert()
      this.hideDropdown()
      this.alertMessage = message;
      this.shouldShowAlert = true;
    },
    resetAlert() {
      this.shouldShowAlert = false;
      this.alertMessage = '';
    },
  },
})
</script>

<style scoped>
.px-7px {
  padding-left: 7px;
  padding-right: 7px;
}
</style>
